export interface ControlCenterKPI {
  id: string;
  label: string;
  value: string;
  change: string;
  changeDirection: 'up' | 'down' | 'neutral';
  timeframe: string;
  icon: string;
  badgeBg: string;
  badgeTextColor: string;
  statusText?: string;
  statusDotColor?: string;
}

export interface ActionRequiredItem {
  id: string;
  title: string;
  domain: string;
  team: string;
  severity: 'CRITICAL' | 'WARNING' | 'INFO';
  dotColor: 'red' | 'orange' | 'grey' | 'blue';
  assignedTo?: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED';
  createdAt: string;
  details?: string;
}

export interface ChangeEvent {
  id: string;
  type: 'RELEASE' | 'MARKET' | 'RULESET' | 'PROVIDER' | 'FEATURE_FLAG';
  title: string;
  description: string;
  actor: string;
  timestamp: string;
  status: 'SUCCESS' | 'PENDING' | 'CANARY';
}

export interface OperationalServiceHealth {
  id: string;
  name: string;
  subtext: string;
  latencyMs?: number;
  status: 'HEALTHY' | 'DEGRADED' | 'DOWN' | 'SYNCING';
  statusText: string;
}

export interface UsageTrendPoint {
  date: string;
  calculations: number;
  activeUsers: number;
  apiRequests: number;
  latencyMs: number;
}

export interface ControlCenterState {
  timeFilter: 'Today' | '7D' | '30D' | 'Custom';
  kpis: ControlCenterKPI[];
  actionsRequired: ActionRequiredItem[];
  recentChanges: ChangeEvent[];
  operationalHealth: OperationalServiceHealth[];
  usageTrend: UsageTrendPoint[];
  unreadNotificationsCount: number;
  pendingApprovalsCount: number;
  systemHealthStatus: 'ALL_OPERATIONAL' | 'DEGRADED' | 'CRITICAL';
  environment: 'PRODUCTION' | 'STAGING' | 'CANARY';
  adminProfile: {
    name: string;
    email: string;
    role: string;
    avatarUrl?: string;
    mfaStatus: boolean;
  };
}

const INITIAL_KPIS: ControlCenterKPI[] = [
  {
    id: 'total_users',
    label: 'Total Users',
    value: '1,248,592',
    change: '2.4%',
    changeDirection: 'up',
    timeframe: 'vs last 7 days',
    icon: 'users',
    badgeBg: 'bg-blue-50',
    badgeTextColor: 'text-blue-600',
  },
  {
    id: 'active_calculations',
    label: 'Active Calculations (24h)',
    value: '482.3K',
    change: '8.1%',
    changeDirection: 'up',
    timeframe: 'vs prior 24h',
    icon: 'calculator',
    badgeBg: 'bg-amber-50',
    badgeTextColor: 'text-amber-600',
  },
  {
    id: 'calculation_volume',
    label: 'Calculation Volume (7d)',
    value: '4.82M',
    change: '0.2%',
    changeDirection: 'neutral',
    timeframe: 'vs last 7 days',
    icon: 'trending-up',
    badgeBg: 'bg-[#EEF4FF]',
    badgeTextColor: 'text-[#2563EB]',
  },
  {
    id: 'api_success_rate',
    label: 'API Success Rate',
    value: '99.98%',
    change: 'Healthy',
    changeDirection: 'up',
    timeframe: 'Across all regions',
    icon: 'check-circle-2',
    badgeBg: 'bg-emerald-50',
    badgeTextColor: 'text-emerald-600',
    statusText: 'Healthy',
    statusDotColor: 'bg-emerald-500',
  },
];

const INITIAL_ACTIONS: ActionRequiredItem[] = [
  {
    id: 'act-001',
    title: 'Critical security incident detected',
    domain: 'Authentication / Identity',
    team: 'Unassigned',
    severity: 'CRITICAL',
    dotColor: 'red',
    status: 'OPEN',
    createdAt: '12 minutes ago',
    details: 'Anomalous credential stuffing pattern detected on legacy OIDC endpoint.',
  },
  {
    id: 'act-002',
    title: '3 stale financial data sources',
    domain: 'Market Data Pipeline',
    team: 'Data Ops Team',
    severity: 'WARNING',
    dotColor: 'red',
    status: 'OPEN',
    createdAt: '45 minutes ago',
    details: 'Barchart & ECB secondary FX sync feeds exceeded 15-minute refresh SLAs.',
  },
  {
    id: 'act-003',
    title: '2 support SLA breaches',
    domain: 'Customer Support',
    team: 'Enterprise Tier',
    severity: 'WARNING',
    dotColor: 'red',
    status: 'OPEN',
    createdAt: '1 hour ago',
    details: '2 Enterprise escalation tickets for custom tax engine rules exceeded 2-hour response window.',
  },
  {
    id: 'act-004',
    title: '1 billing reconciliation exception',
    domain: 'Finance / Invoicing',
    team: 'Automated Review',
    severity: 'INFO',
    dotColor: 'grey',
    status: 'OPEN',
    createdAt: '2 hours ago',
    details: 'Discrepancy of $14.50 between Stripe Webhook and internal ledger batch #4802.',
  },
  {
    id: 'act-005',
    title: '4 content reviews due',
    domain: 'Compliance',
    team: 'Legal Team',
    severity: 'INFO',
    dotColor: 'grey',
    status: 'OPEN',
    createdAt: '3 hours ago',
    details: '2026 France TVA and Canada GST/HST rule updates pending final legal sign-off.',
  },
];

const INITIAL_CHANGES: ChangeEvent[] = [
  {
    id: 'chg-001',
    type: 'RELEASE',
    title: 'PWA Engine v1.19.0 deployed to production',
    description: 'Deterministic offline calculation cache & Service Worker sync enabled.',
    actor: 'DevOps Pipeline',
    timestamp: '15 min ago',
    status: 'SUCCESS',
  },
  {
    id: 'chg-002',
    type: 'MARKET',
    title: 'EU-West-3 Frankfurt region activated',
    description: 'Sub-15ms latency verified across all Eurozone calculation nodes.',
    actor: 'Sarah SRE',
    timestamp: '1 hour ago',
    status: 'SUCCESS',
  },
  {
    id: 'chg-003',
    type: 'RULESET',
    title: '2026 IRS Tax & VAT Tables v4.2 published',
    description: 'Updated tax brackets for California, New York, and Ontario W-2 engines.',
    actor: 'Data Steward Team',
    timestamp: '3 hours ago',
    status: 'SUCCESS',
  },
  {
    id: 'chg-004',
    type: 'PROVIDER',
    title: 'ECB FX Primary Provider failover verified',
    description: 'Automated failover protocol checked with zero rate deviation.',
    actor: 'Currency Engineering',
    timestamp: '5 hours ago',
    status: 'SUCCESS',
  },
  {
    id: 'chg-005',
    type: 'FEATURE_FLAG',
    title: 'enable_realtime_fx_ws switched to CANARY',
    description: '10% of global web traffic routed to WebSocket live rate stream.',
    actor: 'Chief SuperAdmin',
    timestamp: '8 hours ago',
    status: 'CANARY',
  },
];

const INITIAL_HEALTH: OperationalServiceHealth[] = [
  {
    id: 'srv-01',
    name: 'Currency Exchange API',
    subtext: 'Primary Provider',
    latencyMs: 42,
    status: 'HEALTHY',
    statusText: '42ms',
  },
  {
    id: 'srv-02',
    name: 'Core Ledger Database',
    subtext: 'Primary DB Cluster',
    latencyMs: 12,
    status: 'HEALTHY',
    statusText: '12ms',
  },
  {
    id: 'srv-03',
    name: 'FX Ingestion Worker',
    subtext: 'Syncing every 60s',
    status: 'SYNCING',
    statusText: 'Syncing',
  },
  {
    id: 'srv-04',
    name: 'Identity & Auth Engine',
    subtext: 'Passkey & MFA Enforced',
    status: 'HEALTHY',
    statusText: 'Operational',
  },
];

const INITIAL_TREND: UsageTrendPoint[] = [
  { date: 'Aug 21', calculations: 410000, activeUsers: 1150000, apiRequests: 12400000, latencyMs: 22 },
  { date: 'Aug 22', calculations: 435000, activeUsers: 1180000, apiRequests: 13100000, latencyMs: 21 },
  { date: 'Aug 23', calculations: 442000, activeUsers: 1195000, apiRequests: 13400000, latencyMs: 24 },
  { date: 'Aug 24', calculations: 460000, activeUsers: 1210000, apiRequests: 13800000, latencyMs: 19 },
  { date: 'Aug 25', calculations: 471000, activeUsers: 1230000, apiRequests: 14000000, latencyMs: 20 },
  { date: 'Aug 26', calculations: 479000, activeUsers: 1242000, apiRequests: 14150000, latencyMs: 18 },
  { date: 'Aug 27', calculations: 482300, activeUsers: 1248592, apiRequests: 14200000, latencyMs: 18 },
];

let globalState: ControlCenterState = {
  timeFilter: '7D',
  kpis: INITIAL_KPIS,
  actionsRequired: INITIAL_ACTIONS,
  recentChanges: INITIAL_CHANGES,
  operationalHealth: INITIAL_HEALTH,
  usageTrend: INITIAL_TREND,
  unreadNotificationsCount: 3,
  pendingApprovalsCount: 2,
  systemHealthStatus: 'ALL_OPERATIONAL',
  environment: 'PRODUCTION',
  adminProfile: {
    name: 'Super Admin',
    email: 'admin.super@yamoopay.com',
    role: 'SuperAdmin',
    mfaStatus: true,
  },
};

type Listener = () => void;
const listeners: Set<Listener> = new Set();

export function getControlCenterState(): ControlCenterState {
  return globalState;
}

export function subscribeControlCenter(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  listeners.forEach((l) => l());
}

export function setTimeFilter(filter: 'Today' | '7D' | '30D' | 'Custom') {
  globalState = { ...globalState, timeFilter: filter };
  notify();
}

export function resolveActionRequired(id: string) {
  globalState = {
    ...globalState,
    actionsRequired: globalState.actionsRequired.map((act) =>
      act.id === id ? { ...act, status: 'RESOLVED' as const } : act
    ),
  };
  notify();
}

export function assignActionRequired(id: string, assignee: string) {
  globalState = {
    ...globalState,
    actionsRequired: globalState.actionsRequired.map((act) =>
      act.id === id ? { ...act, assignedTo: assignee, status: 'IN_PROGRESS' as const } : act
    ),
  };
  notify();
}

export function addAuditEventToChanges(event: Omit<ChangeEvent, 'id' | 'timestamp'>) {
  const newEvent: ChangeEvent = {
    ...event,
    id: `chg-${Date.now()}`,
    timestamp: 'Just now',
  };
  globalState = {
    ...globalState,
    recentChanges: [newEvent, ...globalState.recentChanges],
  };
  notify();
}
